import {RetentionQuery} from 'common/report/retention/models/retention-query';
import {
  ClientQueryTrackingProps,
  commonTrackingEventPropertyName,
  QueryTrackingPropName,
} from 'common/report/tracking-setup';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {getNumCustomBucketGroupBys} from 'common/report/tracking-setup';
import {
  RetentionTrackingEvents as Events,
  RetentionTrackingProperties as Properties,
} from 'common/report/retention/util/constants';
import {FullWidthReport} from 'common/widgets/fullwidth-report';
import {isSegmentationFilter} from 'common/widgets/property-filter-menu/models/segfilter';

export {Events, Properties};

export function getQueryTrackingProperties(query: RetentionQuery): ClientQueryTrackingProps {
  const countingMethod =
    query.retentionType === `addiction`
      ? query.cumulative
        ? Properties.Cumulative
        : Properties.NonCumulative
      : query.unbounded
      ? Properties.Unbounded
      : Properties.Bounded;

  const {filters} = query;
  const cohorts = query.getCohorts();

  return {
    born_event: query.bornEvent,
    event: query.event,
    retention_type: query.retentionType,
    [QueryTrackingPropName.ChartType]: query.chartType,
    'Custom buckets': query.bucketSizes.length > 1,
    'date range': DateRange.fromBookmarkFormat(query.dateRange).getLabel(),
    'Date unit': query.dateUnit,
    'From date': query.dateRange.from_date,
    'To date': query.dateRange.to_date,
    segments: query.groupBy.length,
    'counting method': countingMethod,
    '# of cohorts in retention query': cohorts ? cohorts.length : 0,
    [commonTrackingEventPropertyName.numCustomBucketGroupBys]: getNumCustomBucketGroupBys(query.groupBy),
    [commonTrackingEventPropertyName.dataGroupId]: query.dataGroupId,
    [commonTrackingEventPropertyName.filterByGroupProperty]: !!(
      filters &&
      filters.some((filter) =>
        Boolean(isSegmentationFilter(filter) ? filter.dataGroupId : filter.property.data_group_id),
      )
    ),
    [commonTrackingEventPropertyName.segmentByGroupProperty]: query.isSegmentedByDataGroup(),
    [commonTrackingEventPropertyName.reportName]: `retention`,
    ...(query.bucketSizes.length ? {'# custom buckets': query.bucketSizes.length} : {}),
    ...getLookupPropertiesTrackingData(query),
    [commonTrackingEventPropertyName.sortConfig]: query.sorting,
    canUseResizableLayout: FullWidthReport.canUseResizingLayout(),
  };
}

function getLookupPropertiesTrackingData(query) {
  const isLookupProperty = (prop) => {
    const hasDataGroupId = !!(prop.data_group_id || prop.dataGroupId);
    const value = prop.propertyName || prop.name;
    return hasDataGroupId && Array.isArray(value);
  };

  const globalFilters = query.filterByEvent.children && query.filterByEvent.children.map((f) => Object.values(f)[0]);
  const allFilters = [
    ...(query.filters?.map((f) => (isSegmentationFilter(f) ? f : f.property)) || []),
    ...(query.bornFilters?.map((f) => (isSegmentationFilter(f) ? f : f.property)) || []),
    ...(globalFilters || []),
  ];
  const numberOfLookupPropertyFilters = allFilters.filter(isLookupProperty).length;
  const numberOfLookupTablePropertyGroupBys = query.groupBy
    ? query.groupBy.reduce((acc, groupBy) => acc + Object.values(groupBy).filter(isLookupProperty).length, 0)
    : 0;
  return {
    [commonTrackingEventPropertyName.numberOfLookupTablePropertyFilters]: numberOfLookupPropertyFilters,
    [commonTrackingEventPropertyName.numberOfLookupTablePropertyGroupBys]: numberOfLookupTablePropertyGroupBys,
    [commonTrackingEventPropertyName.usedLookupTableProperties]:
      numberOfLookupPropertyFilters + numberOfLookupTablePropertyGroupBys > 0,
  };
}
